import { Background, BackgroundColor, Container, Spacer, SpacerVariant, spacerOptions } from "../components/layout"
import React, { useEffect, useState } from "react"
import { Repeater, Text, types, usePageValues } from "react-bricks/frontend"

import Badges from "../components/product/badges"
import ProductImage from "../components/product/image"
import { TabProductProps } from "./TabProduct"
import classNames from "classnames"
import { useLocalesState } from "../../shared/context/locales"
import { usePageContext } from "../../context/page"
import { useQuery } from "@tanstack/react-query"
import { useSDK } from "../../shared/context/client"

export interface TabbedProductsProps {
    sku: string
    repeater?: {
        id: string
        props: TabProductProps
        type: "SingleProduct"
    }[]
    spacerTop: boolean
    spacerBottom: boolean
    variantTop?: SpacerVariant
    variantBottom?: SpacerVariant
    id?: string
}

const TabbedProducts: types.Brick<TabbedProductsProps> = ({
    repeater,
    sku,
    spacerTop,
    spacerBottom,
    variantTop,
    variantBottom,
    id,
}) => {
    const { region } = useLocalesState()
    const { products: productsBuildTime, page: pageContext } = usePageContext()
    const client = useSDK()

    const [pageValues] = usePageValues()

    const page = pageContext || pageValues

    const { data } = useQuery({
        queryKey: ["products", ...(repeater?.map(r => r.props.sku).filter(r => r) || []), region, page.language],
        queryFn: ({ signal }) =>
            client.getProducts({ sku: repeater?.map(r => r.props.sku).filter(r => r), country: region }, page.language, signal),
        enabled: !!repeater?.length,
        meta: { background: true },
    })

    const products = data || productsBuildTime

    const [activeSku, setActiveSku] = useState(sku)

    const clickTab = (sku: string) => {
        setActiveSku(sku)
    }

    useEffect(() => {
        if (!repeater?.length) return
        if (repeater.find(r => r.props.sku === sku)) return setActiveSku(sku)

        setActiveSku(repeater[0].props.sku)
    }, [repeater, sku])

    const findProduct = (sku: string) => {
        return products?.find(p => p.sku === sku)
    }

    return (
        <Background color={BackgroundColor.Grey}>
            <Container>
                <Spacer variant={!spacerTop ? SpacerVariant.None : variantTop} />
                <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-x-5 lg:gap-x-6" id={id}>
                    <div className="hidden sm:block">
                        <div className="sticky top-20 rounded-base bg-white p-5 lg:top-[104px] lg:p-7.5">
                            <ProductImage product={findProduct(activeSku)} />
                            {(repeater?.find(r => r.props.sku === activeSku)?.props.badge ||
                                repeater?.find(r => r.props.sku === activeSku)?.props.secondaryBadge) && (
                                <Badges
                                    badge={repeater?.find(r => r.props.sku === activeSku)?.props.badge}
                                    secondaryBadge={
                                        repeater?.find(r => r.props.sku === activeSku)?.props.secondaryBadge
                                    }
                                />
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col">
                        {repeater && repeater.length > 1 && (
                            <ul className="flex flex-row gap-2.5 rounded-t-lg bg-calypso px-2.5 pt-2.5 text-white sm:px-3.75 md:px-5">
                                {repeater.map(r => {
                                    const product = findProduct(r.props.sku)
                                    return (
                                        <li
                                            key={r.id}
                                            className={classNames(
                                                "flex w-1/2 items-center justify-center rounded-t-base p-2.5 text-center text-base font-semibold sm:p-3.75 sm:text-md",
                                                {
                                                    "bg-white text-blue": activeSku === r.props.sku,
                                                }
                                            )}
                                        >
                                            <Text
                                                propName={`title-${r.props.sku}`}
                                                renderBlock={({ children }) => (
                                                    <button
                                                        className="h-full w-full"
                                                        onClick={() => clickTab(r.props.sku)}
                                                    >
                                                        {children}
                                                    </button>
                                                )}
                                                renderPlaceholder={({ children }) => (
                                                    <button
                                                        className="h-full w-full"
                                                        onClick={() => clickTab(r.props.sku)}
                                                    >
                                                        {children}
                                                    </button>
                                                )}
                                                placeholder={product?.name.short || ""}
                                            />
                                        </li>
                                    )
                                })}
                            </ul>
                        )}
                        <div
                            className={classNames({
                                "rounded-b-lg bg-white": repeater && repeater.length > 1,
                            })}
                        >
                            <Repeater
                                propName="repeater"
                                itemProps={
                                    {
                                        activeSku,
                                        products,
                                    } as TabProductProps
                                }
                            />
                        </div>
                    </div>
                </div>
                <Spacer variant={!spacerBottom ? SpacerVariant.None : variantBottom} />
            </Container>
        </Background>
    )
}

TabbedProducts.schema = {
    name: "TabbedProducts",
    label: "Tabbed PDP (old)",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/TabbedProducts.png`,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "SingleProduct",
                    label: "Product",
                    min: 1,
                    max: 2,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "sku",
            label: "aktive SKU",
            type: types.SideEditPropType.Text,
        },
        {
            name: "id",
            label: "ID",
            type: types.SideEditPropType.Text,
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantTop",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptions,
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerTop === true,
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantBottom",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptions,
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerBottom === true,
                },
            ],
        },
    ],
    getDefaultProps: (): TabbedProductsProps => ({
        sku: "LV500",
        spacerTop: true,
        spacerBottom: true,
        variantTop: SpacerVariant.Base,
        variantBottom: SpacerVariant.Base,
    }),
}

export default TabbedProducts
